import { ethers } from "ethers";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../joinsplit/types";
import { deriveSpendingKeys } from "../joinsplit/keys";
import { hashMpkHex } from "../utils/format";
import {
  decryptInboxNote,
  encryptInboxNoteWithPublicKey,
  formatInboxPublicKeyHex,
  getInboxSecretKeyCandidates,
  getInboxViewingKeyCandidates,
} from "../utils/inbox-crypto";
import { buildInboxNoteRecordId } from "../api/private-data-storage";
import { validateFlatInboxCandidate } from "./inboxProtection";
import { journalBackupDigest } from "./journalBackup";
import {
  exportFlatJournalBackup,
  importFlatJournalBackup,
  listFlatJoinSplitV2SettlementJournal,
  validateFlatJournalBackup,
  type FlatJournalBackup,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";

export const RECOVERY_FILE_MAX_BYTES = 16 * 1024 * 1024;
const MAX_ITEMS = 5000;
const DOMAIN = "stealthos-flat-recovery-file-v1";
export type InboxLane = "pending" | "protected" | "archive";
export type RecoveryEnvelope = {
  ciphertext: string;
  iv: string;
  authTag: string;
  ephemeralPubKey: string;
  commitment?: string;
  recordId?: string;
  deliveryId?: string;
  storageClass?: string;
};
export interface RecoveryContext {
  scope: FlatJoinSplitV2JournalScope;
  keys: SpendingKeys;
  baseUrl: string;
  headers: (path: string) => Promise<Record<string, string>>;
  isCurrent: () => boolean;
  signal?: AbortSignal;
}
export interface RecoveryCapacity {
  records: number;
  bytes: number;
  recordLimit: number;
  byteLimit: number;
}
export interface RecoveryStatus {
  success: true;
  scope: {
    chainId: number;
    poolAddress: string;
    recipientKey: string;
    accountAddress: string;
  };
  capacity: Record<InboxLane | "journal", RecoveryCapacity>;
  alerts: {
    code: string;
    lane: string;
    severity: "warning" | "error";
    message: string;
  }[];
  ingestion: { configured: boolean; ready: boolean };
}
type Identity = RecoveryStatus["scope"];
type BackupBody = { journals: FlatJournalBackup[]; notes: RecoveryEnvelope[] };

function current(ctx: RecoveryContext) {
  if (!ctx.isCurrent() || ctx.signal?.aborted)
    throw new Error("Wallet changed; recovery operation cancelled");
}
export async function recoveryIdentity(
  ctx: RecoveryContext,
): Promise<Identity> {
  current(ctx);
  const keys = await deriveSpendingKeys(ctx.scope.spendingKey);
  current(ctx);
  if (keys.mpk !== ctx.keys.mpk)
    throw new Error("Recovery privacy key mismatch");
  const chainId = Number(ctx.scope.chainId);
  if (!Number.isSafeInteger(chainId) || chainId <= 0)
    throw new Error("Invalid recovery chain");
  return {
    chainId,
    poolAddress: ethers.getAddress(ctx.scope.poolAddress).toLowerCase(),
    recipientKey: await hashMpkHex(ethers.toBeHex(keys.mpk, 32)),
    accountAddress: ethers.getAddress(ctx.scope.accountAddress).toLowerCase(),
  };
}
async function requestImpl(
  ctx: RecoveryContext,
  path: string,
  init: RequestInit = {},
) {
  current(ctx);
  const headers = await ctx.headers(path);
  current(ctx);
  if (!headers.Authorization?.startsWith("Bearer "))
    throw new Error("Sign in to access owner recovery storage");
  const response = await fetch(`${ctx.baseUrl}/api/v1${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...headers,
      ...init.headers,
    },
  });
  current(ctx);
  if (!response.ok) {
    // Do not reflect untrusted service text or secrets into the UI.
    const action =
      response.status === 413
        ? "Capacity reached; nothing was deleted. Keep your backup and retry after capacity is increased."
        : response.status === 429
          ? "Request limit reached; keep your backup and retry later."
          : response.status === 401 || response.status === 403
            ? "Sign in again with the original wallet."
            : "Service unavailable; keep your backup and retry. Partial completed imports are retained.";
    throw new Error(`Recovery request failed (${response.status}). ${action}`);
  }
  let raw: string;
  if (response.body) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let length = 0;
    const chunks: string[] = [];
    try {
      while (true) {
        current(ctx);
        const chunk = await reader.read();
        if (chunk.done) break;
        length += chunk.value.byteLength;
        if (length > RECOVERY_FILE_MAX_BYTES)
          throw new Error("Recovery response exceeds size limit");
        chunks.push(decoder.decode(chunk.value, { stream: true }));
      }
      raw = chunks.join("") + decoder.decode();
    } finally {
      await reader.cancel().catch(() => undefined);
      reader.releaseLock();
    }
  } else raw = await response.text();
  current(ctx);
  if (raw.length > RECOVERY_FILE_MAX_BYTES)
    throw new Error("Recovery response exceeds size limit");
  const data = JSON.parse(raw);
  if (data?.success !== true)
    throw new Error("Recovery service did not acknowledge the operation");
  return data;
}
async function request(
  ctx: RecoveryContext,
  path: string,
  init: RequestInit = {},
) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (ctx.signal?.aborted) abort();
  ctx.signal?.addEventListener("abort", abort, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 30_000);
  try {
    return await requestImpl({ ...ctx, signal: controller.signal }, path, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (timedOut)
      throw new Error(
        "Recovery request timed out; keep the original backup and retry. Completed imports are retained.",
      );
    throw error;
  } finally {
    clearTimeout(timer);
    ctx.signal?.removeEventListener("abort", abort);
  }
}
function sameIdentity(a: Identity, b: Identity) {
  return (
    a?.chainId === b.chainId &&
    a.poolAddress === b.poolAddress &&
    a.accountAddress === b.accountAddress &&
    a.recipientKey === b.recipientKey
  );
}
export function capacitySeverity(
  cap: RecoveryCapacity,
): "ok" | "warning" | "error" {
  const fraction = Math.max(
    cap.records / cap.recordLimit,
    cap.bytes / cap.byteLimit,
  );
  return fraction >= 1 ? "error" : fraction >= 0.8 ? "warning" : "ok";
}
export async function fetchRecoveryStatus(
  ctx: RecoveryContext,
): Promise<RecoveryStatus> {
  const identity = await recoveryIdentity(ctx);
  const data = await request(ctx, `/recovery/status/${identity.recipientKey}`);
  if (
    !sameIdentity(data.scope, identity) ||
    typeof data.ingestion?.configured !== "boolean" ||
    typeof data.ingestion?.ready !== "boolean" ||
    !Array.isArray(data.alerts) ||
    data.alerts.length > 32 ||
    data.alerts.some(
      (alert: any) =>
        !alert ||
        !["warning", "error"].includes(alert.severity) ||
        typeof alert.message !== "string" ||
        alert.message.length > 500,
    )
  )
    throw new Error("Recovery status namespace or shape mismatch");
  for (const lane of ["pending", "protected", "archive", "journal"] as const) {
    const cap = data.capacity?.[lane];
    if (
      !cap ||
      ![cap.records, cap.bytes, cap.recordLimit, cap.byteLimit].every(
        Number.isSafeInteger,
      ) ||
      cap.records < 0 ||
      cap.bytes < 0 ||
      cap.recordLimit <= 0 ||
      cap.byteLimit <= 0
    )
      throw new Error("Malformed recovery capacity");
  }
  return data;
}
export async function registerRecovery(ctx: RecoveryContext): Promise<void> {
  const identity = await recoveryIdentity(ctx);
  await request(ctx, "/recovery/register", {
    method: "POST",
    body: JSON.stringify({
      recoveryVersion: 2,
      chainId: identity.chainId,
      poolAddress: identity.poolAddress,
      recipient_key: identity.recipientKey,
    }),
  });
}
function checkEnvelope(note: RecoveryEnvelope): void {
  if (
    !note ||
    typeof note !== "object" ||
    (note.commitment !== undefined &&
      !/^(?:0x)?[0-9a-fA-F]{64}$/.test(note.commitment)) ||
    !/^[0-9a-fA-F]{24}$/.test(note.iv) ||
    !/^[0-9a-fA-F]{32}$/.test(note.authTag) ||
    !/^(?:[0-9a-fA-F]{2}){1,8192}$/.test(note.ciphertext) ||
    !/^(inbox-v1|inbox-v2:[0-9a-fA-F]{64}|[0-9a-fA-F]{64})$/.test(
      note.ephemeralPubKey,
    )
  )
    throw new Error("Malformed encrypted recovery note");
}
export async function fetchInboxPage(
  ctx: RecoveryContext,
  lane: InboxLane,
  cursor?: string,
): Promise<{ notes: RecoveryEnvelope[]; nextCursor: string | null }> {
  if (cursor !== undefined && !/^\d+$/.test(cursor))
    throw new Error("Invalid recovery cursor");
  const { recipientKey } = await recoveryIdentity(ctx);
  const data = await request(
    ctx,
    `/notes/${lane}/${recipientKey}${cursor ? `?cursor=${cursor}` : ""}`,
  );
  if (
    !Array.isArray(data.notes) ||
    data.notes.length > 100 ||
    (data.nextCursor !== null &&
      (typeof data.nextCursor !== "string" ||
        !/^\d+$/.test(data.nextCursor) ||
        data.nextCursor === cursor))
  )
    throw new Error("Malformed recovery page");
  for (const note of data.notes) {
    checkEnvelope(note);
    if (
      note.storageClass !== lane ||
      typeof note.deliveryId !== "string" ||
      !/^[0-9a-f]{64}$/.test(note.deliveryId)
    )
      throw new Error("Recovery inbox lane mismatch");
  }
  return { notes: data.notes, nextCursor: data.nextCursor };
}
export async function archiveInboxCandidate(
  ctx: RecoveryContext,
  note: RecoveryEnvelope,
) {
  checkEnvelope(note);
  if (
    note.storageClass !== "pending" ||
    !/^[0-9a-f]{64}$/.test(note.deliveryId || "")
  )
    throw new Error("Only a pending delivery can be archived");
  const { recipientKey } = await recoveryIdentity(ctx);
  const result = await request(ctx, "/notes/archive", {
    method: "POST",
    body: JSON.stringify({
      recipient: recipientKey,
      deliveryId: note.deliveryId,
    }),
  });
  if (
    result.deliveryId !== note.deliveryId ||
    result.storageClass !== "archive"
  )
    throw new Error("Archive acknowledgement mismatch");
}
export async function openRecoveryNote(
  ctx: RecoveryContext,
  enc: RecoveryEnvelope,
): Promise<JoinSplitNote> {
  checkEnvelope(enc);
  const note = await decryptInboxNote(
    enc,
    getInboxViewingKeyCandidates(ctx.scope.spendingKey),
    undefined,
    getInboxSecretKeyCandidates(ctx.scope.spendingKey),
  );
  current(ctx);
  await validateFlatInboxCandidate(
    { enc, note },
    {
      chainId: Number(ctx.scope.chainId),
      poolAddress: ctx.scope.poolAddress,
      mpk: ctx.keys.mpk,
    },
  );
  current(ctx);
  return note;
}
async function fileKey(
  ctx: RecoveryContext,
  identity: Identity,
): Promise<CryptoKey> {
  // Separate purpose from journal encryption and authenticate the complete namespace.
  const material = await crypto.subtle.importKey(
    "raw",
    new Uint8Array(ctx.scope.spendingKey),
    "HKDF",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new TextEncoder().encode(DOMAIN),
      info: new TextEncoder().encode(JSON.stringify(identity)),
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}
function hexBytes(value: string) {
  return new Uint8Array(ethers.getBytes(value));
}
async function seal(
  ctx: RecoveryContext,
  identity: Identity,
  body: BackupBody,
): Promise<string> {
  const plaintext = JSON.stringify(body);
  if (plaintext.length > RECOVERY_FILE_MAX_BYTES / 2 - 4096)
    throw new Error(
      "Recovery export exceeds 16 MiB limit; keep existing backups and contact the operator",
    );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const header = { format: DOMAIN, ...identity };
  const ciphertext = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: new TextEncoder().encode(JSON.stringify(header)),
    },
    await fileKey(ctx, identity),
    new TextEncoder().encode(plaintext),
  );
  current(ctx);
  return JSON.stringify({
    ...header,
    iv: ethers.hexlify(iv),
    ciphertext: ethers.hexlify(new Uint8Array(ciphertext)),
  });
}
/** Export never deletes or changes a delivery. Raw inbox ciphertext may include junk. */
export async function createRecoveryFile(
  ctx: RecoveryContext,
  localNotes: readonly StoredJoinSplitNote[],
): Promise<string> {
  const identity = await recoveryIdentity(ctx);
  const body: BackupBody = { journals: [], notes: [] };
  const journalIds = new Set<string>();
  // One database snapshot prevents lane moves between page reads from omitting mail.
  const data = await request(ctx, `/recovery/export/${identity.recipientKey}`);
  if (
    !sameIdentity(data.scope, identity) ||
    !Array.isArray(data.records) ||
    !Array.isArray(data.notes) ||
    data.records.length > MAX_ITEMS ||
    data.notes.length > MAX_ITEMS
  )
    throw new Error("Recovery snapshot namespace, shape or size mismatch");
  for (const item of data.records) {
    if (
      item.id !== item.record?.id ||
      item.digest !== journalBackupDigest(item.record) ||
      journalIds.has(item.id)
    )
      throw new Error("Recovery journal digest or duplicate mismatch");
    await validateFlatJournalBackup(ctx.scope, item.record);
    current(ctx);
    journalIds.add(item.id);
    body.journals.push(item.record);
  }
  const deliveryIds = new Set<string>();
  for (const note of data.notes) {
    checkEnvelope(note);
    if (
      !/^[0-9a-f]{64}$/.test(note.deliveryId) ||
      deliveryIds.has(note.deliveryId) ||
      !["pending", "protected", "archive"].includes(note.storageClass)
    )
      throw new Error("Malformed or duplicate snapshot delivery");
    deliveryIds.add(note.deliveryId);
    body.notes.push(note);
  }
  // A prepared local record may exist even when its initial remote PUT failed.
  for (const record of await listFlatJoinSplitV2SettlementJournal(ctx.scope)) {
    if (record.state === "prepared" && !journalIds.has(record.id))
      body.journals.push(await exportFlatJournalBackup(ctx.scope, record.id));
  }
  if (body.journals.length > MAX_ITEMS)
    throw new Error("Recovery export item limit exceeded");
  const commitments = new Set<string>();
  for (const stored of localNotes) {
    if (
      stored.note.protocol !== "flat-joinsplit-v2" ||
      commitments.has(stored.note.commitment)
    )
      continue;
    await validateFlatInboxCandidate(
      { enc: {}, note: stored.note },
      { ...identity, mpk: ctx.keys.mpk },
    );
    current(ctx);
    commitments.add(stored.note.commitment);
    body.notes.push({
      ...(await encryptInboxNoteWithPublicKey(
        stored.note,
        formatInboxPublicKeyHex(ctx.scope.spendingKey),
      )),
      commitment: stored.note.commitment,
      recordId: await buildInboxNoteRecordId(
        identity.recipientKey,
        stored.note.commitment,
      ),
    });
    if (body.notes.length > MAX_ITEMS)
      throw new Error("Recovery export item limit exceeded");
  }
  return seal(ctx, identity, body);
}
export async function inspectRecoveryFile(
  ctx: RecoveryContext,
  raw: string,
): Promise<{
  journals: FlatJournalBackup[];
  candidates: { enc: RecoveryEnvelope; note: JoinSplitNote }[];
  skipped: number;
}> {
  if (new TextEncoder().encode(raw).length > RECOVERY_FILE_MAX_BYTES)
    throw new Error("Recovery file exceeds 16 MiB limit");
  const file = JSON.parse(raw);
  const identity = await recoveryIdentity(ctx);
  if (
    file.format !== DOMAIN ||
    !sameIdentity(file, identity) ||
    !/^0x[0-9a-f]{24}$/.test(file.iv) ||
    !/^0x(?:[0-9a-f]{2}){16,}$/.test(file.ciphertext)
  )
    throw new Error("Recovery file wallet, chain, pool or format mismatch");
  const header = { format: DOMAIN, ...identity };
  let body: BackupBody;
  try {
    const plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: hexBytes(file.iv),
        additionalData: new TextEncoder().encode(JSON.stringify(header)),
      },
      await fileKey(ctx, identity),
      hexBytes(file.ciphertext),
    );
    body = JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    throw new Error(
      "Recovery file authentication failed; use the original privacy credentials",
    );
  }
  current(ctx);
  if (
    !Array.isArray(body.journals) ||
    !Array.isArray(body.notes) ||
    body.journals.length > MAX_ITEMS ||
    body.notes.length > MAX_ITEMS
  )
    throw new Error("Recovery file item limit or shape is invalid");
  const ids = new Set<string>();
  for (const backup of body.journals) {
    if (ids.has(backup.id)) throw new Error("Duplicate recovery journal ID");
    ids.add(backup.id);
    await validateFlatJournalBackup(ctx.scope, backup);
    current(ctx);
  }
  const candidates: { enc: RecoveryEnvelope; note: JoinSplitNote }[] = [];
  let skipped = 0;
  for (const enc of body.notes) {
    checkEnvelope(enc);
    try {
      candidates.push({ enc, note: await openRecoveryNote(ctx, enc) });
    } catch {
      current(ctx);
      skipped++;
    } // Pending mail may legitimately be junk; never import it as a private note.
  }
  return { journals: body.journals, candidates, skipped };
}
export async function restoreRecoveryFile(
  ctx: RecoveryContext,
  raw: string,
  restoreNotes: (
    candidates: { enc: RecoveryEnvelope; note: JoinSplitNote }[],
    isCurrent: () => boolean,
    signal?: AbortSignal,
  ) => Promise<number>,
): Promise<{ journals: number; notes: number; skipped: number }> {
  // Authenticate and validate the entire file before the first mutation.
  const inspected = await inspectRecoveryFile(ctx, raw);
  let journals = 0;
  for (const backup of inspected.journals) {
    current(ctx);
    if (await importFlatJournalBackup(ctx.scope, backup, ctx.isCurrent))
      journals++;
  }
  current(ctx);
  const notes = await restoreNotes(
    inspected.candidates,
    ctx.isCurrent,
    ctx.signal,
  );
  current(ctx);
  return { journals, notes, skipped: inspected.skipped };
}

/** Restore only after the caller's chain check; no blind protected-lane write. */
export async function replayRecoveryNote(
  ctx: RecoveryContext,
  enc: RecoveryEnvelope,
): Promise<{ deliveryId: string; storageClass: string }> {
  const note = await openRecoveryNote(ctx, enc);
  const identity = await recoveryIdentity(ctx);
  const payload = {
    ephemeralPubKey: enc.ephemeralPubKey,
    ciphertext: enc.ciphertext,
    iv: enc.iv,
    authTag: enc.authTag,
    commitment: note.commitment,
    recordId:
      enc.recordId ||
      (await buildInboxNoteRecordId(identity.recipientKey, note.commitment)),
  };
  const idempotencyKey = `recovery-${ethers.sha256(ethers.toUtf8Bytes(JSON.stringify(payload))).slice(2, 50)}`;
  const result = await request(ctx, "/notes/store", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify({ recipient: identity.recipientKey, note: payload }),
  });
  const digest = ethers
    .sha256(
      ethers.toUtf8Bytes(
        JSON.stringify({
          ephemeralPubKey: payload.ephemeralPubKey,
          ciphertext: payload.ciphertext,
          iv: payload.iv,
          authTag: payload.authTag,
        }),
      ),
    )
    .slice(2);
  if (
    result.ciphertextDigest !== digest ||
    !/^[0-9a-f]{64}$/.test(result.deliveryId) ||
    (enc.deliveryId && result.deliveryId !== enc.deliveryId) ||
    !["pending", "protected", "archive"].includes(result.storageClass)
  )
    throw new Error("Recovery delivery acknowledgement mismatch");
  return result;
}
